import { useEffect, useRef } from 'react';
import { Box, Stack } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import JsBarcode from 'jsbarcode';
import { useTranslation } from 'react-i18next';
import { formatSequenceValue } from '@/lib/autoGenerate';
import type { AutoGenerateConfig, BarcodeSymbology, LabelObject } from '@/types/labelTemplate';

const SYMBOLOGY_MAP: Record<BarcodeSymbology, string> = {
  CODE128: 'CODE128',
  CODE39: 'CODE39',
  EAN13: 'EAN13',
  UPC: 'UPC',
  PDF417: 'CODE128', // fallback since jsbarcode has no native PDF417
};

/** Resolves {{Placeholder}} tokens against sample data for live preview purposes. */
export function resolveTokens(text = '', sampleData: Record<string, string> = {}): string {
  return text.replace(/\{\{?(\w+)\}?\}/g, (_, key: string) => sampleData[key] ?? `{${key}}`);
}

/**
 * An auto-generating object still carrying its config has no real value yet, so the configured
 * start value formatted with its prefix/suffix/padding is shown — a true preview of the pattern
 * without pretending it is a reserved value. This is the DESIGN-time path.
 *
 * Anything printing or previewing an actual physical label must call applySequenceValue first,
 * which stamps that label's own value on and clears `autoGenerate` so it lands in the
 * resolveTokens branch below. Substituting the value while leaving `autoGenerate` set silently
 * does nothing — every label then renders the same start value regardless of what was reserved
 * for it, which is exactly the bug that put identical barcodes on a whole print run.
 */
function resolveValue(rawValue: string, autoGenerate: AutoGenerateConfig | undefined, sampleData: Record<string, string>): string {
  return autoGenerate ? formatSequenceValue(autoGenerate, autoGenerate.startValue) : resolveTokens(rawValue, sampleData);
}

function BarcodeSvg({ data, symbology, humanReadable }: { data: string; symbology: BarcodeSymbology; humanReadable?: boolean }) {
  const ref = useRef<SVGSVGElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    try {
      JsBarcode(ref.current, data || '000000', {
        format: SYMBOLOGY_MAP[symbology] || 'CODE128',
        displayValue: humanReadable !== false,
        height: 40,
        margin: 4,
        fontSize: 12,
      });
    } catch {
      // Ignore invalid data while user is typing
    }
  }, [data, symbology, humanReadable]);
  return <svg ref={ref} style={{ width: '100%', height: '100%' }} />;
}

interface LabelObjectRendererProps {
  object: LabelObject;
  sampleData: Record<string, string>;
  selected?: boolean;
  onSelect?: (id: string, e: React.MouseEvent) => void;
  scale: number;
}

export default function LabelObjectRenderer({ object, sampleData, selected, onSelect, scale }: LabelObjectRendererProps) {
  const { t } = useTranslation('labelDesigner');
  const style: React.CSSProperties = {
    position: 'absolute',
    left: object.x * scale,
    top: object.y * scale,
    width: object.w * scale,
    height: object.h * scale,
    // Without this, a bound field's resolved text (often longer than the placeholder text the
    // box was originally sized for) visually spills past this object's own bounds and over
    // whatever sits below it — looking like two objects got selected together when only this
    // one actually is. Clipping to the declared box also matches what a real printer does: it
    // won't render outside the object's own footprint either.
    overflow: 'hidden',
    outline: selected ? '2px solid #004ac6' : '1px dashed transparent',
    outlineOffset: 2,
    cursor: 'move',
    display: 'flex',
    alignItems: object.type === 'text' ? 'flex-start' : 'center',
    justifyContent: object.type === 'text' && object.align === 'center' ? 'center' : 'flex-start',
    boxSizing: 'border-box',
  };

  const commonProps = {
    style,
    onMouseDown: (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelect?.(object.id, e);
    },
  };

  switch (object.type) {
    case 'text':
      return (
        <Box {...commonProps}>
          <span
            style={{
              fontSize: (object.fontSize || 12) * (scale / 3),
              fontWeight: object.fontWeight || 400,
              color: object.color || '#1a1c1c',
              whiteSpace: 'pre-wrap',
              textAlign: object.align || 'left',
              width: '100%',
            }}
          >
            {resolveValue(object.text, object.autoGenerate, sampleData)}
          </span>
        </Box>
      );
    case 'barcode':
      return (
        <Box {...commonProps}>
          <BarcodeSvg data={resolveValue(object.data, object.autoGenerate, sampleData)} symbology={object.symbology} humanReadable={object.humanReadable} />
        </Box>
      );
    case 'qr':
      return (
        <Box {...commonProps}>
          <QRCodeSVG value={resolveValue(object.data, object.autoGenerate, sampleData) || 'DATA'} size={Math.min(object.w, object.h) * scale} level={object.errorCorrection || 'M'} />
        </Box>
      );
    case 'rectangle':
      return (
        <Box
          {...commonProps}
          style={{
            ...style,
            border: `${object.borderWidth || 2}px solid ${object.borderColor || '#414752'}`,
            background: object.background || 'transparent',
            borderRadius: object.shape === 'ellipse' ? '50%' : 0,
          }}
        />
      );
    case 'line':
      return (
        <Box {...commonProps} style={{ ...style, alignItems: 'center' }}>
          <Box sx={{ width: '100%', borderTop: `${object.borderWidth || 2}px solid ${object.borderColor || '#414752'}` }} />
        </Box>
      );
    case 'logo':
    case 'image':
      return (
        <Box
          {...commonProps}
          sx={{
            ...style,
            bgcolor: object.src ? 'transparent' : 'action.hover',
            border: object.src ? 'none' : '1px dashed',
            borderColor: 'divider',
            fontSize: 11,
            color: 'text.secondary',
          }}
        >
          {object.src ? (
            <Box component="img" src={object.src} alt="" sx={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
          ) : (
            t('canvas.logoPlaceholder')
          )}
        </Box>
      );
    case 'table':
      // One row per selected field, stacked as "Label: Value" lines against the single current
      // record (sampleData) — the field pool offered while designing depends on the template's
      // own assignmentType (see PropertiesPanel.tsx's bindableFieldsFor), but rendering here is the
      // same regardless.
      return (
        <Box {...commonProps} style={{ ...style, alignItems: 'flex-start', flexDirection: 'column', overflowY: 'auto' }}>
          <Stack sx={{ width: '100%' }}>
            {object.fields.length === 0 && (
              <span style={{ fontSize: 11 * (scale / 3), color: '#9aa0a6', fontStyle: 'italic' }}>{t('canvas.tableNoFields')}</span>
            )}
            {object.fields.map((field) => (
              <span
                key={field}
                style={{
                  fontSize: (object.fontSize || 11) * (scale / 3),
                  height: object.rowHeight * scale,
                  lineHeight: `${object.rowHeight * scale}px`,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {resolveTokens(object.showLabels ? `${field}: {{${field}}}` : `{{${field}}}`, sampleData)}
              </span>
            ))}
          </Stack>
        </Box>
      );
    default:
      return null;
  }
}
